import { useCallback, useEffect, useState } from "react";
import type { OwnerInvitation, TeamMember, TeamRole, VoiceModel, Webhook } from "./types";
import type { StoredScope } from "../organisation/access-model";
import { useAuth } from "../../context/AuthContext";
import * as settingsApi from "./settings-api";

/** TODO(MIGRATION): swap remaining mock settings for real endpoints. UI must not change. */

export function useSettings() {
  const [orgName, setOrgName] = useState("TechLadder AI");
  const [orgTimezone, setOrgTimezone] = useState("Asia/Kolkata");

  const [webhooks, setWebhooks] = useState<Webhook[]>([
    { id: "w1", url: "https://crm.techladder.ai/hooks/call-events", events: ["call.completed", "call.failed"], active: true, lastDelivery: "4 mins ago" },
  ]);

  const [voices, setVoices] = useState<(VoiceModel & { isDefault: boolean })[]>([
    { id: "v1", name: "Anaya", gender: "Female", languages: ["HI", "EN"], style: "Warm · Conversational", isDefault: true },
    { id: "v2", name: "Raj", gender: "Male", languages: ["HI", "EN"], style: "Authoritative · Collections", isDefault: false },
    { id: "v3", name: "Kashufa", gender: "Female", languages: ["HI", "UR", "EN"], style: "Calm · Support", isDefault: false },
  ]);

  const { account, token } = useAuth();
  const orgId = account?.organization_id;
  const accountId = account?.id;
  const [members, setMembers] = useState<TeamMember[]>([]);

  const loadMembers = useCallback(async () => {
    if (!orgId || !accountId || !token) return;

    return Promise.all([
      settingsApi.getOrganizationMembers(orgId, token),
      settingsApi.getAccountMembers(accountId, token),
    ])
      .then(([organizationMembers, accountMembers]: [any[], any[]]) => {
        const accountMemberships = new Map(
          accountMembers.map((membership) => [membership.user_id, membership]),
        );

        setMembers(organizationMembers.map((organizationMember) => {
          const accountMembership = accountMemberships.get(organizationMember.user_id) as any;
          const isInvited = organizationMember.status === "invited";

          return {
            id: organizationMember.user_id,
            name: organizationMember.name || organizationMember.email?.split("@")[0] || "Unknown User",
            email: organizationMember.email || "",
            role: (isInvited ? organizationMember.role_id : accountMembership?.role_id || organizationMember.role_id) as TeamRole,
            status: organizationMember.status,
            permissions: isInvited
              ? organizationMember.permissions || []
              : accountMembership?.permissions || [],
            // Voice access lives on the user document, so it comes from the
            // organization membership even for an active member.
            allowedVoices: organizationMember.allowed_voices || [],
            // WhatsApp numbers: null (never configured) means every number.
            allowedWhatsAppInstances: organizationMember.allowed_whatsapp_instances ?? null,
            version: organizationMember.version ?? null,
            resourceScope: organizationMember.resource_scope ?? null,
          };
        }));
      })
      .catch(console.error);
  }, [accountId, orgId, token]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  const addWebhook = (url: string, events: string[]) =>
    setWebhooks((prev) => [{ id: `w${Date.now()}`, url, events, active: true, lastDelivery: null }, ...prev]);
  const toggleWebhook = (id: string) =>
    setWebhooks((prev) => prev.map((w) => (w.id === id ? { ...w, active: !w.active } : w)));
  const deleteWebhook = (id: string) => setWebhooks((prev) => prev.filter((w) => w.id !== id));

  const setDefaultVoice = (id: string) =>
    setVoices((prev) => prev.map((v) => ({ ...v, isDefault: v.id === id })));

  const setMemberRole = async (id: string, role: TeamRole) => {
    if (!orgId || !token) throw new Error("Organisation context is unavailable");

    const member = members.find((candidate) => candidate.id === id);
    if (!member) throw new Error("Member not found");

    try {
      if (member.status === "invited") {
        await settingsApi.updateInvitationAccess(orgId, id, { role_id: role }, token);
      } else {
        await settingsApi.updateMemberRole(orgId, id, role, token);
      }
      setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, role } : m)));
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const removeMember = async (id: string) => {
    if (!orgId || !token) throw new Error("Organisation context is unavailable");
    try {
      await settingsApi.removeOrganizationMember(orgId, id, token);
      setMembers((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const inviteMember = async (
    email: string,
    role: TeamRole,
    access?: { permissions: string[]; resource_scope: StoredScope | null },
  ): Promise<OwnerInvitation> => {
    if (!orgId || !token) throw new Error("Organisation context is unavailable");
    try {
      const invite = await settingsApi.inviteOrganizationMember(orgId, email, role, token, access);
      setMembers((prev) => [...prev, {
        id: invite.id,
        name: email.split("@")[0],
        email,
        role: invite.role_id as TeamRole,
        status: "invited",
        permissions: invite.permissions || [],
      }]);
      return invite;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const setMemberVoices = async (id: string, allowedVoices: string[]) => {
    if (!orgId || !token) throw new Error("Organisation context is unavailable");
    await settingsApi.updateMemberVoiceAccess(orgId, id, allowedVoices, token);
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, allowedVoices } : m)));
  };

  const setMemberWhatsAppInstances = async (id: string, allowedWhatsAppInstances: string[]) => {
    if (!orgId || !token) throw new Error("Organisation context is unavailable");
    await settingsApi.updateMemberWhatsAppAccess(orgId, id, allowedWhatsAppInstances, token);
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, allowedWhatsAppInstances } : m)));
  };

  const setMemberPermissions = async (id: string, permissions: string[]) => {
    if (!orgId || !accountId || !token) throw new Error("Organisation context is unavailable");

    const member = members.find((candidate) => candidate.id === id);
    if (!member) throw new Error("Member not found");

    try {
      if (member.status === "invited") {
        await settingsApi.updateInvitationAccess(orgId, id, { permissions }, token);
      } else {
        await settingsApi.updateMemberPermissions(accountId, id, permissions, token);
      }
      setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, permissions } : m)));
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  /** member_grants: role, inline grants and scope in one versioned write. A 409 means
   *  someone else changed this member, so the list is reloaded before rethrowing. */
  const saveMemberAccess = async (id: string, access: settingsApi.MemberAccessWrite) => {
    if (!orgId || !token) throw new Error("Organisation context is unavailable");
    try {
      const row = await settingsApi.setMemberAccess(orgId, id, access, token);
      setMembers((prev) => prev.map((m) => (m.id === id ? {
        ...m,
        role: (row?.role_id ?? access.role_id) as TeamRole,
        permissions: row?.permissions ?? access.permissions,
        version: row?.version ?? m.version,
        resourceScope: row?.resource_scope ?? m.resourceScope,
      } : m)));
    } catch (err) {
      if ((err as any)?.response?.status === 409) await loadMembers();
      throw err;
    }
  };

  const setMemberStatus = async (id: string, status: "active" | "suspended") => {
    if (!orgId || !token) throw new Error("Organisation context is unavailable");
    const row = await settingsApi.setMemberStatus(orgId, id, status, token);
    setMembers((prev) => prev.map((m) => (m.id === id ? {
      ...m, status: row?.status ?? status, version: row?.version ?? m.version,
    } : m)));
  };

  return {
    orgName, setOrgName, orgTimezone, setOrgTimezone,
    webhooks, addWebhook, toggleWebhook, deleteWebhook,
    voices, setDefaultVoice,
    members, setMemberRole, removeMember, inviteMember, setMemberPermissions, setMemberVoices,
    setMemberWhatsAppInstances, saveMemberAccess, setMemberStatus, reloadMembers: loadMembers,
  };
}
